import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { config } from "dotenv";
import App from "./App";
import CustomApolloProvider from "./components/CustomApolloProvider";
import { ModalContextProvider } from "./context/modalContext";
import * as serviceWorker from "./serviceWorker";
import { info, error as logError } from "./services/logging-service";

/**
 * Production-ready error handling
 * Suppress console logs in production but log errors to service
 */
if (process.env.NODE_ENV === "production") {
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.log = () => {};
    console.debug = () => {};
    console.error = (...args) => {
        logError("Console Error", { args });
        originalConsoleError.apply(console, args);
    };
    console.warn = (...args) => {
        info("Console Warning", { args });
        originalConsoleWarn.apply(console, args);
    };
}

// Handle unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
    logError("Unhandled Promise Rejection", {
        reason: event.reason,
        promise: event.promise,
    });
    console.error("Unhandled promise rejection:", event.reason);
});

// Handle global errors
window.addEventListener("error", (event) => {
    logError("Global Error", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
    });
});

config();

const container = document.getElementById("app");

if (!container) {
    throw new Error("Failed to find the root element");
}

// Suppress Apollo Client deprecation warnings in development
if (process.env.NODE_ENV === "development") {
    const originalWarn = console.warn;
    console.warn = (...args) => {
        // Suppress Apollo useLazyQuery deprecation warnings
        if (args[0]?.includes?.("useLazyQuery") || args[0]?.includes?.("go.apollo.dev/c/err")) {
            return;
        }
        originalWarn.apply(console, args);
    };
}

const root = createRoot(container);

info("Application starting", {
    environment: process.env.NODE_ENV,
    userAgent: navigator.userAgent,
});

// Disable StrictMode in development to prevent double WebSocket connections
// StrictMode intentionally double-invokes effects to find bugs
const AppWrapper =
    process.env.NODE_ENV === "production" ? (
        <React.StrictMode>
            <BrowserRouter
                future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                }}
            >
                <ModalContextProvider>
                    <CustomApolloProvider>
                        <App />
                    </CustomApolloProvider>
                </ModalContextProvider>
            </BrowserRouter>
        </React.StrictMode>
    ) : (
        <BrowserRouter
            future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
            }}
        >
            <ModalContextProvider>
                <CustomApolloProvider>
                    <App />
                </CustomApolloProvider>
            </ModalContextProvider>
        </BrowserRouter>
    );

root.render(AppWrapper);

// Register service worker for PWA capabilities
serviceWorker.register({
    onSuccess: () => info("Service worker registered successfully"),
    onUpdate: () => info("Service worker updated"),
});
